import axios, { Method, ResponseType } from "axios";
import mapKnownValues from "../utils/mapKnownValues";

// SEE: https://dev-employer-api.dev-manifest.com/swagger-ui/index.html

class CoreAPI {
  baseUrl = "";
  employerSearchUrl = "";
  authorizedCheck: () => boolean = () => false;
  getSessionToken: () => string | undefined = () => undefined;
  redirectAndThrowError: () => any = () => {};
  useSentry = false;

  constructor(
    params: Pick<
      CoreAPI,
      "useSentry" | "baseUrl" | "employerSearchUrl" | "authorizedCheck" | "getSessionToken" | "redirectAndThrowError"
    >
  ) {
    mapKnownValues(this, params);
  }

  private unauthorizedAPICheck(e) {
    if (e.response && (e.response.status === 401 || e.response.statusText === "Unauthorized")) {
      this.redirectAndThrowError();
    }
  }

  private async makeCall<T>({
    includeAuthorization = true,
    responseType = "" as ResponseType | undefined,
    method = "GET" as Method,
    url = "",
    body = {},
    params = {},
    parseDataProperty = true,
  }) {
    const authHeaders: Record<string, string> = {};
    if (includeAuthorization && this.authorizedCheck()) {
      authHeaders.Authorization = `Bearer ${this.getSessionToken()}`;
    }
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await axios({
        method,
        url,
        params,
        responseType,
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          ...authHeaders,
        },
        data: body,
      });
      if (parseDataProperty && response.data.data) {
        return response.data.data as T;
      }
      return response.data as T;
    } catch (e: any) {
      throw e;
    }
  }

  async authCall<T>({
    method = "GET" as Method,
    responseType = undefined as ResponseType | undefined,
    endpoint = "",
    body = {},
    params = {},
  }) {
    if (!this.authorizedCheck()) this.redirectAndThrowError();
    try {
      return await this.makeCall<T>({
        includeAuthorization: true,
        method,
        responseType,
        url: `${this.baseUrl}${endpoint}`,
        body,
        params,
      });
    } catch (e) {
      this.unauthorizedAPICheck(e);
      throw e;
    }
  }

  async call<T>({
    responseType = undefined as ResponseType | undefined,
    method = "GET" as Method,
    endpoint = "",
    body = {},
    params = {},
  }) {
    return this.makeCall<T>({
      method,
      responseType,
      includeAuthorization: false,
      url: `${this.baseUrl}${endpoint}`,
      body,
      params,
    });
  }

  async callEmployerSearch<T>({ method = "GET" as Method, endpoint = "", body = {}, params = {} }) {
    if (!this.authorizedCheck()) this.redirectAndThrowError();
    try {
      return await this.makeCall<T>({
        includeAuthorization: true,
        method,
        url: `${this.employerSearchUrl}${endpoint}`,
        body,
        params,
        parseDataProperty: false,
      });
    } catch (e) {
      this.unauthorizedAPICheck(e);
      throw e;
    }
  }
}

export default CoreAPI;
